import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FaCommentDots, FaEdit, FaPlus, FaSave, FaWhatsapp } from "react-icons/fa";
import { apihubGet, apihubPost } from "../../api/apihub";
import { useProjectPhone } from "../../hooks/useProjectPhone";

const toKeywordRows = (words = []) =>
  words.length > 0 ? words.map((value) => ({ value })) : [{ value: "" }];

const defaultResponsePrefs = () => ({
  template: {},
  regular: { messageType: "text", message: "" },
  type: "regular",
});

const normalizeResponsePrefs = (prefs) => {
  if (!prefs) return defaultResponsePrefs();
  const regular = {
    messageType: "text",
    ...prefs.regular,
    message: prefs.regular?.message || prefs.regular?.text || "",
  };
  const type =
    prefs.type ||
    (prefs.template?.templateAssociated ? "template" : "regular");
  return {
    ...defaultResponsePrefs(),
    ...prefs,
    regular,
    type,
  };
};

const extractMessagePreview = (prefs) => {
  const normalized = normalizeResponsePrefs(prefs);
  if (normalized.type === "template") {
    const template = normalized.template?.templateAssociated;
    if (template?.components?.length) {
      const header = template.components.find((c) => c.type === "HEADER");
      const body = template.components.find((c) => c.type === "BODY");
      const buttons = template.components.find((c) => c.type === "BUTTONS");
      return {
        name: template.name,
        header: header?.text || "",
        body: body?.text || "",
        buttons: buttons?.buttons?.map((b) => b.text).filter(Boolean) || [],
      };
    }
  }
  const text = normalized.regular?.message || normalized.regular?.text || "";
  if (text) {
    return { body: text };
  }
  return null;
};

const Toggle = ({ checked, onChange, disabled, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    className={`greeting-toggle${checked ? " is-on" : ""}`}
    onClick={() => onChange(!checked)}
    disabled={disabled}
  />
);

const ResponsePreview = ({ preview }) => (
  <div className="greeting-preview optin-response-preview">
    <FaWhatsapp className="greeting-preview-wa" aria-hidden />
    {preview?.body || preview?.header ? (
      <div className="greeting-preview-body">
        {preview.header ? (
          <p className="greeting-preview-header">{preview.header}</p>
        ) : null}
        {preview.body ? (
          <p className="greeting-preview-text">{preview.body}</p>
        ) : null}
      </div>
    ) : (
      <p className="greeting-preview-empty">No response message configured yet.</p>
    )}
  </div>
);

const ResponseCard = ({
  title,
  hint,
  enabled,
  onToggle,
  onEdit,
  preview,
  disabled,
}) => (
  <article className="optin-response-card">
    <div className="optin-response-card-head">
      <div>
        <h3>{title}</h3>
        <p>{hint}</p>
      </div>
      <div className="optin-response-card-actions">
        <Toggle
          checked={enabled}
          onChange={onToggle}
          disabled={disabled}
          label={`Toggle ${title}`}
        />
        <button
          type="button"
          className="greeting-edit-btn"
          onClick={onEdit}
          disabled={disabled}
        >
          <FaEdit aria-hidden />
          Edit
        </button>
      </div>
    </div>
    <ResponsePreview preview={preview} />
  </article>
);

const OptInOutManagement = () => {
  const { projectId, phoneNoId } = useProjectPhone();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [maxOptOut, setMaxOptOut] = useState(5);
  const [maxOptIn, setMaxOptIn] = useState(5);
  const [doNotSendCampaignToOptedOut, setDoNotSendCampaignToOptedOut] =
    useState(true);
  const [sendOptOutResponse, setSendOptOutResponse] = useState(true);
  const [sendOptInResponse, setSendOptInResponse] = useState(true);
  const [optOutKeywords, setOptOutKeywords] = useState([{ value: "" }]);
  const [optInKeywords, setOptInKeywords] = useState([{ value: "" }]);
  const [optOutResponsePrefs, setOptOutResponsePrefs] = useState(null);
  const [optInResponsePrefs, setOptInResponsePrefs] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError("");
    try {
      const res = await apihubGet(
        `/getInteractedCustomerDetail/getoptinoutsetting/${projectId}`,
      );
      const s = res?.optInOutSettings || res?.data?.optInOutSettings || res;
      if (!s) return;
      setMaxOptOut(s.noOfOptOutKeyWords ?? 5);
      setMaxOptIn(s.noOfOptInKeyWords ?? 5);
      setDoNotSendCampaignToOptedOut(Boolean(s.doNotSendCampaignToOptedOut));
      setSendOptOutResponse(Boolean(s.sendOptOutResponse));
      setSendOptInResponse(Boolean(s.sendOptInResponse));
      setOptOutKeywords(toKeywordRows(s.optOutKeyWords));
      setOptInKeywords(toKeywordRows(s.optInKeyWords));
      setOptOutResponsePrefs(normalizeResponsePrefs(s.optOutResponsePreferences));
      setOptInResponsePrefs(normalizeResponsePrefs(s.optInResponsePreferences));
    } catch (err) {
      setError(err.message || "Failed to load opt-in settings");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const fetchTemplates = useCallback(async () => {
    if (!projectId) return;
    setLoadingTemplates(true);
    try {
      const qs = new URLSearchParams({
        page: "1",
        limit: "50",
        sortBy: "createdAt",
        sortOrder: "desc",
      });
      const phoneForPath = phoneNoId || "0";
      const res = await apihubGet(
        `/getInteractedCustomerDetail/gettotaltemplatesbyphonenumberid/${phoneForPath}?${qs}&projectId=${encodeURIComponent(projectId)}`,
      );
      setTemplates((res?.data || []).filter((t) => t.template_status === "APPROVED"));
    } catch {
      setTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  }, [projectId, phoneNoId]);

  useEffect(() => {
    if (!editModal) {
      setEditDraft(null);
      return;
    }
    const source =
      editModal === "out" ? optOutResponsePrefs : optInResponsePrefs;
    setEditDraft(normalizeResponsePrefs(source));
    fetchTemplates();
  }, [editModal, optOutResponsePrefs, optInResponsePrefs, fetchTemplates]);

  const optOutPreview = useMemo(
    () => extractMessagePreview(optOutResponsePrefs),
    [optOutResponsePrefs],
  );
  const optInPreview = useMemo(
    () => extractMessagePreview(optInResponsePrefs),
    [optInResponsePrefs],
  );
  const editPreview = useMemo(
    () => extractMessagePreview(editDraft),
    [editDraft],
  );

  const buildPayload = (overrides = {}) => ({
    doNotSendCampaignToOptedOut,
    sendOptOutResponse,
    optOutKeyWords: optOutKeywords.map((k) => k.value).filter(Boolean),
    optOutResponsePreferences: optOutResponsePrefs,
    sendOptInResponse,
    optInKeyWords: optInKeywords.map((k) => k.value).filter(Boolean),
    optInResponsePreferences: optInResponsePrefs,
    noOfOptOutKeyWords: maxOptOut,
    noOfOptInKeyWords: maxOptIn,
    ...overrides,
  });

  const persistSettings = async (overrides = {}) => {
    if (!projectId) return;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const payload = buildPayload(overrides);
      const res = await apihubPost(
        `/getInteractedCustomerDetail/updateuseroptinoutsetting/${projectId}`,
        payload,
      );
      if (overrides.doNotSendCampaignToOptedOut !== undefined) {
        setDoNotSendCampaignToOptedOut(overrides.doNotSendCampaignToOptedOut);
      }
      if (overrides.sendOptOutResponse !== undefined) {
        setSendOptOutResponse(overrides.sendOptOutResponse);
      }
      if (overrides.sendOptInResponse !== undefined) {
        setSendOptInResponse(overrides.sendOptInResponse);
      }
      if (overrides.optOutResponsePreferences !== undefined) {
        setOptOutResponsePrefs(
          normalizeResponsePrefs(overrides.optOutResponsePreferences),
        );
      }
      if (overrides.optInResponsePreferences !== undefined) {
        setOptInResponsePrefs(
          normalizeResponsePrefs(overrides.optInResponsePreferences),
        );
      }
      setMessage(res?.message || "Settings saved successfully");
    } catch (err) {
      setError(err.message || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const updateKeyword = (list, setList, index, value) => {
    const next = [...list];
    next[index] = { value };
    setList(next);
  };

  const addKeyword = (list, setList, max) => {
    if (list.length >= max) return;
    setList([...list, { value: "" }]);
  };

  const setDraftType = (type) => {
    setEditDraft((prev) => normalizeResponsePrefs({ ...prev, type }));
  };

  const setDraftRegularMessage = (message) => {
    setEditDraft((prev) =>
      normalizeResponsePrefs({
        ...prev,
        type: "regular",
        regular: { messageType: "text", message },
      }),
    );
  };

  const setDraftTemplate = (templateId) => {
    const template = templates.find((t) => t._id === templateId);
    if (!template) {
      setEditDraft((prev) =>
        normalizeResponsePrefs({
          ...prev,
          type: "regular",
          template: {},
        }),
      );
      return;
    }
    setEditDraft((prev) =>
      normalizeResponsePrefs({
        ...prev,
        type: "template",
        template: { templateAssociated: template },
      }),
    );
  };

  const saveResponseDraft = async () => {
    if (!editDraft || !editModal) return;
    const key =
      editModal === "out"
        ? "optOutResponsePreferences"
        : "optInResponsePreferences";
    await persistSettings({ [key]: editDraft });
    setEditModal(null);
  };

  if (loading) {
    return (
      <div className="optin-page">
        <p className="optin-loading">Loading opt-in settings…</p>
      </div>
    );
  }

  const editTitle =
    editModal === "out" ? "Opt-out Response" : "Opt-in Response";
  const selectedTemplateId =
    editDraft?.template?.templateAssociated?._id || "";

  return (
    <div className="optin-page">
      <header className="optin-header">
        <h1>
          <FaCommentDots aria-hidden />
          Opt-in Management
        </h1>
        <p className="optin-subtitle">
          You can configure the system to automatically resolve cases when a user
          has not responded for more than 24 hours, allowing you to close inactive
          conversations without manual intervention.
        </p>
      </header>

      {error && <p className="optin-alert optin-alert-error">{error}</p>}
      {message && <p className="optin-alert optin-alert-success">{message}</p>}

      <section className="optin-api-card">
        <div>
          <h2>API Campaign Opt-out</h2>
          <p>
            Enable this if you don&apos;t wish to send API campaign to opted-out
            contacts.
          </p>
        </div>
        <Toggle
          checked={doNotSendCampaignToOptedOut}
          onChange={(next) =>
            persistSettings({ doNotSendCampaignToOptedOut: next })
          }
          disabled={saving}
          label="API Campaign Opt-out"
        />
      </section>

      <section className="optin-keywords-panel">
        <div className="optin-keywords-col">
          <h2>Opt-out Keywords</h2>
          <p className="optin-keywords-hint">
            The user will have to type exactly one of these messages on which they
            should be automatically opted-out.
          </p>
          <div className="optin-keyword-inputs">
            {optOutKeywords.map((row, i) => (
              <input
                key={i}
                type="text"
                value={row.value}
                onChange={(e) =>
                  updateKeyword(
                    optOutKeywords,
                    setOptOutKeywords,
                    i,
                    e.target.value,
                  )
                }
                placeholder={i === 0 ? "STOP" : "Keyword"}
                aria-label={`Opt-out keyword ${i + 1}`}
              />
            ))}
          </div>
          <div className="optin-keywords-actions">
            {optOutKeywords.length < maxOptOut && (
              <button
                type="button"
                className="optin-add-btn"
                onClick={() =>
                  addKeyword(optOutKeywords, setOptOutKeywords, maxOptOut)
                }
                disabled={saving}
              >
                <FaPlus aria-hidden />
                Add More
              </button>
            )}
            <button
              type="button"
              className="optin-save-btn"
              onClick={() => persistSettings()}
              disabled={saving}
            >
              <FaSave aria-hidden />
              {saving ? "Saving…" : "Save Setting"}
            </button>
          </div>
        </div>
        <ResponseCard
          title="Opt-out Response"
          hint="Setup a response message for opt-out user keywords"
          enabled={sendOptOutResponse}
          onToggle={(next) => persistSettings({ sendOptOutResponse: next })}
          onEdit={() => setEditModal("out")}
          preview={optOutPreview}
          disabled={saving}
        />
      </section>

      <section className="optin-keywords-panel">
        <div className="optin-keywords-col">
          <h2>Opt-in Keywords</h2>
          <p className="optin-keywords-hint">
            The user will have to type exactly one of these messages on which they
            should be automatically opted-in.
          </p>
          <div className="optin-keyword-inputs optin-keyword-inputs-numbered">
            {optInKeywords.map((row, i) => (
              <div key={i} className="optin-keyword-row">
                <span className="optin-keyword-num">{i + 1}.</span>
                <input
                  type="text"
                  value={row.value}
                  onChange={(e) =>
                    updateKeyword(
                      optInKeywords,
                      setOptInKeywords,
                      i,
                      e.target.value,
                    )
                  }
                  placeholder={i === 0 ? "START" : "Keyword"}
                  aria-label={`Opt-in keyword ${i + 1}`}
                />
              </div>
            ))}
          </div>
          {optInKeywords.length < maxOptIn && (
            <button
              type="button"
              className="optin-add-btn optin-add-btn-standalone"
              onClick={() =>
                addKeyword(optInKeywords, setOptInKeywords, maxOptIn)
              }
              disabled={saving}
            >
              <FaPlus aria-hidden />
              Add More
            </button>
          )}
        </div>
        <ResponseCard
          title="Opt-in Response"
          hint="Setup a response message for opt-in user keywords"
          enabled={sendOptInResponse}
          onToggle={(next) => persistSettings({ sendOptInResponse: next })}
          onEdit={() => setEditModal("in")}
          preview={optInPreview}
          disabled={saving}
        />
      </section>

      {editModal && editDraft && (
        <div className="greeting-modal-backdrop" onClick={() => setEditModal(null)}>
          <div
            className="greeting-modal-card optin-edit-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="optin-edit-title"
          >
            <h3 id="optin-edit-title">Edit {editTitle}</h3>
            <p className="greeting-modal-hint">
              Choose a plain text reply or an approved WhatsApp template for the
              automatic keyword response.
            </p>

            <div className="optin-edit-type">
              <label>
                <input
                  type="radio"
                  name="optin-message-type"
                  checked={editDraft.type === "regular"}
                  onChange={() => setDraftType("regular")}
                />
                Regular message
              </label>
              <label>
                <input
                  type="radio"
                  name="optin-message-type"
                  checked={editDraft.type === "template"}
                  onChange={() => setDraftType("template")}
                />
                WhatsApp template
              </label>
            </div>

            {editDraft.type === "regular" ? (
              <label className="optin-edit-field">
                <span>Message</span>
                <textarea
                  rows={5}
                  value={editDraft.regular?.message || ""}
                  onChange={(e) => setDraftRegularMessage(e.target.value)}
                  placeholder="Enter the reply sent when a user matches your keyword…"
                />
              </label>
            ) : (
              <label className="optin-edit-field">
                <span>Approved template</span>
                <select
                  value={selectedTemplateId}
                  onChange={(e) => setDraftTemplate(e.target.value)}
                  disabled={loadingTemplates}
                >
                  <option value="">
                    {loadingTemplates ? "Loading templates…" : "Select a template"}
                  </option>
                  {templates.map((t) => (
                    <option key={t._id} value={t._id}>
                      {t.name} ({t.language || "en"})
                    </option>
                  ))}
                </select>
              </label>
            )}

            <p className="greeting-modal-hint">Preview</p>
            {editPreview?.name && (
              <p className="greeting-modal-template-name">
                Template: <strong>{editPreview.name}</strong>
              </p>
            )}
            <ResponsePreview preview={editPreview} />

            <div className="greeting-modal-actions optin-edit-actions">
              <button
                type="button"
                className="greeting-modal-btn"
                onClick={() => setEditModal(null)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="optin-save-btn"
                onClick={saveResponseDraft}
                disabled={saving}
              >
                <FaSave aria-hidden />
                {saving ? "Saving…" : "Save Response"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OptInOutManagement;
